import { useCallback } from 'react'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import log from 'loglevel'
import {
  CreateControlManualEvidenceRequest,
  CreateOrphanedManualEvidenceRequest,
  CreateRequestManualEvidenceRequest,
  CreatedEvidenceGroup,
  EvidencePayload,
} from '@trustero/trustero-api-web/lib/evidence/service_pb'
import { EvidenceServicePromiseClient } from '@trustero/trustero-api-web/lib/evidence/service_grpc_web_pb'
import { stripFileExtension } from 'src/components/Reusable/Forms/FileSelector/FileSelector.helpers'
import { useAuthorizedGrpcClientWithContentUpdate } from '../../../adapter/grpcClient'
import { showInfoToast, ToastPrompts } from '../../../Utils/helpers/toast'
import { useInvalidateEvidence } from '../../../Utils/swrCacheInvalidation'
import { useAnalytics } from '../../../analytics/useAnalytics'

type BulkCreateEvidenceArgs = {
  controlId?: string[]
  requestId?: string[]
}

export const useBulkCreateEvidence = ({
  controlId,
  requestId,
}: BulkCreateEvidenceArgs): ((
  files: File[],
  date: Timestamp,
  uploadSize: number,
  caption?: string,
) => Promise<void>) => {
  const invalidateEvidence = useInvalidateEvidence()
  const evidenceClient = useAuthorizedGrpcClientWithContentUpdate(
    EvidenceServicePromiseClient,
  )
  const { track, events } = useAnalytics()

  return async (
    files: File[],
    date: Timestamp,
    uploadSize: number,
    caption?: string,
  ) => {
    const filesCount = files.length
    const evidences: EvidencePayload[] = []

    for (const file of files) {
      const name =
        files.length === 1
          ? caption || stripFileExtension(file?.name)
          : stripFileExtension(file?.name)

      const bodyBytes = new Uint8Array(await new Response(file).arrayBuffer())
      const newEvidence = new EvidencePayload()
        .setMime(file.type)
        .setBody(bodyBytes)
        .setCaption(name)
        .setRelevantDate(date)
        .setFileName(file.name)
      evidences.push(newEvidence)
    }

    try {
      track(events.S_EVIDENCE_UPLOAD, { count: filesCount, uploadSize })
      if (controlId && controlId.length > 0) {
        const request = new CreateControlManualEvidenceRequest()
          .setEvidencePayloadList(evidences)
          .setControlIdsList(controlId)
        await evidenceClient.createControlManualEvidence(request)
      } else if (requestId && requestId.length > 0) {
        const request = new CreateRequestManualEvidenceRequest()
          .setEvidencePayloadList(evidences)
          .setRequestIdsList(requestId)
        await evidenceClient.createRequestManualEvidence(request)
      } else {
        const request =
          new CreateOrphanedManualEvidenceRequest().setEvidencePayloadsList(
            evidences,
          )
        await evidenceClient.createOrphanedManualEvidence(request)
      }

      await invalidateEvidence()
    } catch (error) {
      log.error(
        `Error when adding new evidence in useBulkCreateEvidence hook for ${
          controlId ? `control id: ${controlId}` : `request id: ${requestId}`
        }`,
        error,
      )
      showInfoToast(ToastPrompts.EVIDENCE_UPLOAD_FAILED)
    }
  }
}

export const useCreateOrphanedEvidence = (): ((
  payloads: EvidencePayload[],
) => Promise<CreatedEvidenceGroup[]>) => {
  const evidenceClient = useAuthorizedGrpcClientWithContentUpdate(
    EvidenceServicePromiseClient,
  )

  return useCallback(
    async (payloads: EvidencePayload[]) => {
      const request =
        new CreateOrphanedManualEvidenceRequest().setEvidencePayloadsList(
          payloads,
        )
      const response = await evidenceClient.createOrphanedManualEvidence(
        request,
      )
      return response.getEvidenceGroupsList()
    },
    [evidenceClient],
  )
}

type BulkCreateEvidenceForControlsArgs = {
  payloads: EvidencePayload[]
  controlIds: string[]
}

export const useBulkCreateEvidenceForControls = (): ((
  args: BulkCreateEvidenceForControlsArgs,
) => Promise<void>) => {
  const evidenceClient = useAuthorizedGrpcClientWithContentUpdate(
    EvidenceServicePromiseClient,
  )

  return useCallback(
    async ({ payloads, controlIds }: BulkCreateEvidenceForControlsArgs) => {
      if (controlIds.length === 0) {
        const request =
          new CreateOrphanedManualEvidenceRequest().setEvidencePayloadsList(
            payloads,
          )
        await evidenceClient.createOrphanedManualEvidence(request)
      } else {
        const request = new CreateControlManualEvidenceRequest()
          .setEvidencePayloadList(payloads)
          .setControlIdsList(controlIds)
        await evidenceClient.createControlManualEvidence(request)
      }
    },
    [evidenceClient],
  )
}
